<template>
  <div class="mobile-signup-container">
    <div class="mobile-signup-card">
      <!-- Logo Section -->
      <div class="auth-logo">
        <h1>
          <i class="fas fa-route me-2"></i>
          Gypsy Souls
        </h1>
        <p>Create Your Account</p>
      </div>

      <!-- Signup Form -->
      <div class="auth-step">
        <h4 class="step-title">
          <i class="fas fa-user-plus me-2"></i>
          Complete Your Profile
        </h4>
        <p class="step-subtitle">
          Mobile: +{{ countryCode }}{{ mobileNumber }}
        </p>

        <form @submit.prevent="sendOTP">
          <div class="mb-3">
            <label for="username" class="form-label">Username</label>
            <input
              type="text"
              class="form-control"
              id="username"
              v-model="form.username"
              :class="{ 'is-invalid': errors.username }"
              placeholder="Choose a unique username"
              minlength="3"
              maxlength="50"
              required
              autocomplete="username"
            />
            <div v-if="errors.username" class="invalid-feedback">
              {{ errors.username }}
            </div>
            <small class="form-text text-muted">
              This will be your display name (3-50 characters)
            </small>
          </div>

          <!-- Error Alert -->
          <div v-if="error" class="alert alert-danger" role="alert">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ error }}
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="btn btn-primary w-100"
            :disabled="isLoading"
          >
            <span
              v-if="isLoading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </span>
            <i v-else class="fas fa-paper-plane me-2"></i>
            {{ isLoading ? "Sending..." : "Send OTP" }}
          </button>
        </form>
      </div>

      <!-- Back to Login -->
      <div class="text-center mt-4 pt-3 border-top">
        <p class="text-muted mb-2">Already have an account?</p>
        <router-link to="/login" class="btn btn-outline-secondary btn-sm">
          <i class="fas fa-arrow-left me-2"></i>
          Back to Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import axios from "axios";

export default {
  name: "MobileSignup",
  data() {
    return {
      form: {
        username: "",
      },
      errors: {},
      error: null,
      isLoading: false,
      mobileNumber: "",
      countryCode: "91",
    };
  },

  created() {
    // Get mobile number and country code from query params
    this.mobileNumber = this.$route.query.mobile || "";
    this.countryCode = this.$route.query.countryCode || "91";

    if (!this.mobileNumber) {
      // Redirect to login if no mobile number
      this.$router.push("/login");
    }
  },

  methods: {
    ...mapActions("auth", ["mobileLogin"]),

    validateForm() {
      this.errors = {};

      if (!this.form.username) {
        this.errors.username = "Username is required";
      } else if (this.form.username.length < 3) {
        this.errors.username = "Username must be at least 3 characters";
      } else if (this.form.username.length > 50) {
        this.errors.username = "Username must be less than 50 characters";
      } else if (!/^[a-zA-Z0-9_]+$/.test(this.form.username)) {
        this.errors.username =
          "Username can only contain letters, numbers, and underscores";
      }

      return Object.keys(this.errors).length === 0;
    },

    async sendOTP() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const response = await axios.post("/mobile/auth/send-otp", {
          username: this.form.username,
          mobileNumber: this.mobileNumber,
          countryCode: this.countryCode,
        });

        if (response.data.success) {
          // Redirect to OTP verification page
          this.$router.push({
            name: "MobileOTPVerification",
            query: {
              mobile: this.mobileNumber,
              countryCode: this.countryCode,
              verificationId: response.data.verificationId,
              username: this.form.username,
              isSignup: true,
            },
          });
        } else {
          // Handle specific error cases
          if (response.data.error === "Mobile number already registered") {
            this.error =
              response.data.message || "Mobile number already registered";

            // Show a more user-friendly message with option to go to login
            Swal.fire({
              title: "Mobile Number Already Registered",
              text:
                response.data.message ||
                "This mobile number is already registered. Please use the login option instead.",
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: "Go to Login",
              cancelButtonText: "Try Different Number",
              confirmButtonColor: "#2563eb",
              cancelButtonColor: "#6c757d",
            }).then((result) => {
              if (result.isConfirmed) {
                this.$router.push("/login");
              }
            });
            return;
          }

          throw new Error(response.data.message || "Failed to send OTP");
        }
      } catch (error) {
        console.error("Send OTP error:", error);

        // Handle specific error cases
        if (
          error.response?.data?.error === "Mobile number already registered"
        ) {
          this.error =
            error.response.data.message || "Mobile number already registered";

          // Show a more user-friendly message with option to go to login
          Swal.fire({
            title: "Mobile Number Already Registered",
            text:
              error.response.data.message ||
              "This mobile number is already registered. Please use the login option instead.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Go to Login",
            cancelButtonText: "Try Different Number",
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6c757d",
          }).then((result) => {
            if (result.isConfirmed) {
              this.$router.push("/login");
            }
          });
        } else {
          this.error =
            error.response?.data?.error ||
            error.message ||
            "Failed to send OTP";
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.mobile-signup-container {
  background: linear-gradient(135deg, #b46428 0%, #78501e 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.mobile-signup-card {
  background: #f5f2ed;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(44, 36, 25, 0.15);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  position: relative;
  overflow: hidden;
}

.mobile-signup-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #b46428, #78501e);
}

.auth-logo {
  text-align: center;
  margin-bottom: 2rem;
}

.auth-logo h1 {
  background: linear-gradient(135deg, #b46428, #78501e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.auth-logo p {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
}

.auth-step {
  animation: slideIn 0.3s ease-out;
}

.step-title {
  color: #333;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
}

.step-subtitle {
  color: #6c757d;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
}

.form-control:focus {
  border-color: #b46428;
  box-shadow: 0 0 0 0.2rem rgba(180, 100, 40, 0.25);
}

.btn-primary {
  background: linear-gradient(135deg, #b46428, #78501e);
  border: none;
  font-weight: 600;
  padding: 0.75rem;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #966428, #5a6446);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(180, 100, 40, 0.4);
}

.btn-outline-secondary {
  border-color: #6c757d;
  color: #6c757d;
  border-radius: 10px;
  font-weight: 600;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
}

.alert {
  border-radius: 10px;
  border: none;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.form-text {
  font-size: 0.8rem;
}

.border-top {
  border-color: #e9ecef !important;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 576px) {
  .mobile-signup-card {
    margin: 1rem;
    padding: 1.5rem;
  }

  .auth-logo h1 {
    font-size: 1.75rem;
  }

  .step-title {
    font-size: 1.1rem;
  }
}
</style>
